import { useEffect, useState } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { firebaseConfigured, getFirebase } from '@/firebase';
import type { FeatureFlag } from '@/lib/types';

const FOUNDER_EMAIL =
  import.meta.env.VITE_FOUNDER_EMAIL ?? 'rushangbhardwaj@outlook.com';

function normalize(value: unknown): FeatureFlag | null {
  if (value == null) return null;
  if (typeof value === 'boolean') {
    return { on_for_all: value, on_for_founder: value };
  }
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    return {
      on_for_all: v.on_for_all === true,
      on_for_founder: v.on_for_founder === true,
    };
  }
  return null;
}

// Reads Firestore `factory_config/flags` and pulls the field named
// `{flagName}` — mirrors rta-chat-v2's implementation verbatim so the
// on_for_all/on_for_founder semantics stay identical across both apps.
//
// Defaults to OFF when:
//   - Firebase not configured
//   - doc missing
//   - field missing / null
//   - onSnapshot / getDoc errors (permission denied, offline, etc.)
//
// isOn = on_for_all OR (on_for_founder AND user.email === founder).
export function useFeatureFlag(flagName: string): {
  loading: boolean;
  isOn: boolean;
  reason:
    | 'loading'
    | 'firebase_not_configured'
    | 'default_off_flag_missing'
    | 'off_by_flag'
    | 'on_by_all'
    | 'on_by_founder';
} {
  const [loading, setLoading] = useState(true);
  const [flag, setFlag] = useState<FeatureFlag | null>(null);
  const [flagMissing, setFlagMissing] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userSettled, setUserSettled] = useState(false);

  useEffect(() => {
    if (!firebaseConfigured) {
      setLoading(false);
      setUserSettled(true);
      setFlagMissing(true);
      return;
    }
    const fb = getFirebase();
    if (!fb) {
      setLoading(false);
      setUserSettled(true);
      setFlagMissing(true);
      return;
    }
    const flagRef = doc(fb.db, 'factory_config', 'flags');
    const unsubFlag = onSnapshot(
      flagRef,
      (snap) => {
        const data = snap.data() as Record<string, unknown> | undefined;
        const fieldVal = data ? data[flagName] : undefined;
        const normalized = normalize(fieldVal);
        setFlag(normalized);
        setFlagMissing(!snap.exists() || normalized === null);
        setLoading(false);
      },
      (_err) => {
        setFlag(null);
        setFlagMissing(true);
        setLoading(false);
      },
    );
    const unsubAuth = onAuthStateChanged(fb.auth, (u) => {
      setUser(u);
      setUserSettled(true);
    });
    getDoc(flagRef).catch((_err) => {
    });
    return () => {
      unsubFlag();
      unsubAuth();
    };
  }, [flagName]);

  if (!firebaseConfigured) {
    return { loading: false, isOn: false, reason: 'firebase_not_configured' };
  }
  if (loading || !userSettled) {
    return { loading: true, isOn: false, reason: 'loading' };
  }
  if (flagMissing || !flag) {
    return { loading: false, isOn: false, reason: 'default_off_flag_missing' };
  }
  if (flag.on_for_all) return { loading: false, isOn: true, reason: 'on_by_all' };
  const emailMatch =
    !!user?.email && user.email.toLowerCase() === FOUNDER_EMAIL.toLowerCase();
  if (flag.on_for_founder && emailMatch) {
    return { loading: false, isOn: true, reason: 'on_by_founder' };
  }
  return { loading: false, isOn: false, reason: 'off_by_flag' };
}
